import { c as _c } from "react/compiler-runtime";
import { Alert } from "@flat-ui";
import { Stack } from "@mui-wrapper";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { formatUserRoleType } from "@utils/data-display";
import { isRoleRaisedByTeam } from "@utils/team-utils";
import { getPrettyName } from "@utils/user-utils";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
/**
 * Returns who a note is about: the member by name when only one is added, or the members it
 * applies to otherwise.
 *
 * @param members All the members that are going to be added
 * @param numberOfAffectedMembers How many of them the note applies to
 */
function getSubject(members, numberOfAffectedMembers) {
  if (members.length === 1) {
    return {
      subject: <strong>
          <var>{getPrettyName(members[0])}</var>
        </strong>,
      isSingular: true
    };
  }
  return {
    subject: numberOfAffectedMembers === members.length ? "These members" : "Some of these members",
    isSingular: false
  };
}

/**
 * Points out what adding the members to the team changes beyond the team itself.
 *
 * Joining a team gives its members the workspace role of the team whenever that role is higher
 * than their own, which the members list does not show anywhere. Getting the access of the team
 * is the reason to add someone to it, so that is not pointed out.
 */
export function AddToTeamNotes(t0) {
  const $ = _c(24);
  const {
    team,
    members
  } = t0;
  let t1;
  if ($[0] !== members || $[1] !== team) {
    let t2;
    if ($[3] !== team) {
      t2 = member => isRoleRaisedByTeam(team.role, member.role);
      $[3] = team;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = members.filter(t2);
    $[0] = members;
    $[1] = team;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const numberOfPromotedMembers = t1.length;
  let t2;
  if ($[5] !== members) {
    t2 = members.filter(_temp);
    $[5] = members;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const numberOfPendingMembers = t2.length;
  if (!numberOfPromotedMembers && !numberOfPendingMembers) {
    return null;
  }
  let t3;
  if ($[7] !== members || $[8] !== numberOfPromotedMembers) {
    t3 = getSubject(members, numberOfPromotedMembers);
    $[7] = members;
    $[8] = numberOfPromotedMembers;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const promoted = t3;
  let t4;
  if ($[10] !== members || $[11] !== numberOfPendingMembers) {
    t4 = getSubject(members, numberOfPendingMembers);
    $[10] = members;
    $[11] = numberOfPendingMembers;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const pending = t4;
  let t5;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      gap: 1
    };
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== numberOfPromotedMembers || $[15] !== promoted || $[16] !== team) {
    t6 = numberOfPromotedMembers > 0 && <Alert variant="warning" title={<>{promoted.subject} {promoted.isSingular ? "gets" : "get"} a higher workspace role by joining this {TEAM_DISPLAY_NAME}:{" "}<strong>{formatUserRoleType(team.role)}</strong>.</>} />;
    $[14] = numberOfPromotedMembers;
    $[15] = promoted;
    $[16] = team;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== numberOfPendingMembers || $[19] !== pending) {
    t7 = numberOfPendingMembers > 0 && <Alert variant="info" title={<>{pending.subject} {pending.isSingular ? "hasn't" : "haven't"}{" "}joined the workspace yet and {pending.isSingular ? "gets" : "get"}{" "}the {TEAM_DISPLAY_NAME}'s access once they join.</>} />;
    $[18] = numberOfPendingMembers;
    $[19] = pending;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t6 || $[22] !== t7) {
    t8 = <Stack sx={t5}>{t6}{t7}</Stack>;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  return t8;
}
function _temp(member_0) {
  return !FaroCoreApiUtils.Member.isMemberActive(member_0);
}